import { DeepChat } from 'deep-chat-react-dev'
import { MessageSquare, MoreVertical, SquarePen, Trash2, User, X } from 'lucide-react'
import type React from 'react'
import { type ComponentRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, supabaseUrl } from '../../lib/supabase'
import type { Conversation } from '../../types/database.tables'
import { Button } from '../common/Button'

interface ContextBadge {
  type: 'athlete'
  label: string
  value: string
}

// Styled Components
const Container = styled.div`
  display: flex;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background};
`

const Sidebar = styled.div`
  width: 300px;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.cardBackground};
`

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SidebarTitle = styled.h2`
  margin-left: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.sm};
`

const ConversationItem = styled.div<{ $isActive: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  background-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.surface : 'transparent')};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface};
  }

  &:hover .conversation-menu-button {
    opacity: 1;
  }
`

const ConversationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs};
`

const ConversationTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  flex: 1;
`

const ConversationMenuButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  opacity: 0;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ConversationMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  right: ${({ theme }) => theme.spacing.sm};
  top: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  z-index: 10;
  min-width: 150px;
`

const MenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  text-align: left;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }

  &.danger {
    color: ${({ theme }) => theme.colors.error};
  }
`

const ConversationAthlete = styled.div`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const ConversationPreview = styled.div`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`

const ChatFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.cardBackground};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`

const ChatTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.lg};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  flex: 1;
`

const ContextBadgesContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.xs};
  font-weight: 500;
`

const BadgeRemoveButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7
  }
`

const ChatContainer = styled.div`
  width: 100%;
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
`

const EmptyStateIcon = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg}
  color: ${({ theme }) => theme.colors.textMuted}
`

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.lg}
  margin: 0
`

type DeepChatHandle = ComponentRef<typeof DeepChat>

export const AnalysisView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, session } = useAuth()
  const deepChatRef = useRef<DeepChatHandle>(null)

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [contextBadges, setContextBadges] = useState<ContextBadge[]>([])
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [conversationHistory, setConversationHistory] = useState<any[]>([])
  const hasGeneratedTitleRef = useRef<Set<string>>(new Set())
  const isCreatingConversationRef = useRef(false)
  const isLoadingHistoryRef = useRef(false)
  const savedMessagesRef = useRef<Set<string>>(new Set())
  const chatSessionKeyRef = useRef<string>(Date.now().toString())
  const conversationNotInListRef = useRef<Set<string>>(new Set())

  // Get athlete from URL params if navigating from athlete card
  const athleteIdParam = searchParams.get('athleteId')
  const athleteNameParam = searchParams.get('athleteName')

  // Load conversations on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    loadConversations()
  }, [user])

  // Handle athlete context from URL
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (athleteIdParam && athleteNameParam) {
      // Create new conversation with athlete context
      createNewConversationWithAthlete(athleteIdParam, athleteNameParam)
      // Clear params
      setSearchParams({})
    }
  }, [athleteIdParam, athleteNameParam])

  // Update context badges when active conversation changes
  useEffect(() => {
    if (activeConversationId) {
      const conversation = conversations.find((c) => c.id === activeConversationId)
      if (conversation?.athlete_id && conversation?.athlete_name) {
        setContextBadges([
          {
            type: 'athlete',
            label: conversation.athlete_name,
            value: conversation.athlete_id,
          },
        ])
      } else {
        setContextBadges([])
      }
    }
  }, [activeConversationId, conversations])

  const loadConversations = async () => {
    console.log('loadConversations')
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('ai_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })

      if (error) throw error
      setConversations(data || [])

      // Auto-select first conversation if none selected
      if (data && data.length > 0 && !activeConversationId) {
        const firstConvId = data[0].id
        setActiveConversationId(firstConvId)
        // Load messages for the first conversation
        loadConversationMessages(firstConvId)
      }
    } catch (error) {
      console.error('Error loading conversations:', error)
    }
  }

  const createNewConversation = async () => {
    console.log('createNewConversation')
    if (!user) return

    // Generate a new conversation ID on the client
    const newConversationId = crypto.randomUUID()

    // Set it as active immediately
    setActiveConversationId(newConversationId)
    setContextBadges([])
    setConversationHistory([])
    savedMessagesRef.current.clear()
    chatSessionKeyRef.current = newConversationId
    conversationNotInListRef.current.add(newConversationId)

    // Clear chat history
    if (deepChatRef.current) {
      deepChatRef.current.clearMessages()
    }
  }

  const createNewConversationWithAthlete = async (athleteId: string, athleteName: string) => {
    console.log('createNewConversationWithAthlete:', athleteId, athleteName)
    if (!user) return

    // Generate a new conversation ID on the client
    const newConversationId = crypto.randomUUID()

    // Set it as active immediately
    setActiveConversationId(newConversationId)
    setContextBadges([
      {
        type: 'athlete',
        label: athleteName,
        value: athleteId,
      },
    ])
    setConversationHistory([])
    savedMessagesRef.current.clear()
    chatSessionKeyRef.current = newConversationId
    conversationNotInListRef.current.add(newConversationId)

    // Clear chat history
    if (deepChatRef.current) {
      deepChatRef.current.clearMessages()
    }
  }

  const loadConversationMessages = async (conversationId: string) => {
    console.log('loadConversationMessages:', conversationId)
    try {
      isLoadingHistoryRef.current = true
      savedMessagesRef.current.clear()

      const { data: messages, error } = await supabase
        .from('ai_conversation_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('timestamp', { ascending: true })

      if (error) throw error

      if (messages && messages.length > 0) {
        // Convert database messages to DeepChat format and track them
        const deepChatMessages = messages.map((msg) => {
          savedMessagesRef.current.add(msg.id)
          return {
            id: msg.id,
            text: msg.content,
            role: msg.role === 'assistant' ? 'ai' : msg.role,
          }
        })
        setConversationHistory(deepChatMessages)
      } else {
        setConversationHistory([])
      }

      // Wait a bit for DeepChat to load history before allowing saves again
      setTimeout(() => {
        isLoadingHistoryRef.current = false
      }, 1000)
    } catch (error) {
      console.error('Error loading conversation messages:', error)
      setConversationHistory([])
      isLoadingHistoryRef.current = false
    }
  }

  const saveMessage = async (conversationId: string, role: 'user' | 'assistant', content: string) => {
    console.log('saveMessage:', conversationId, role, content)
    try {
      const { error } = await supabase.from('ai_conversation_messages').insert({
        conversation_id: conversationId,
        role,
        content,
      })

      if (error) throw error
    } catch (error) {
      console.error('Error saving message:', error)
    }
  }

  const createConversationInDB = async (conversationId: string) => {
    console.log('createConversationInDB:', conversationId)
    if (!user || isCreatingConversationRef.current) return false

    isCreatingConversationRef.current = true

    try {
      const athleteContext = contextBadges.find((b) => b.type === 'athlete')

      const { error } = await supabase.from('ai_conversations').insert({
        id: conversationId, // Use the client-generated ID
        user_id: user.id,
        title: 'New Conversation',
        athlete_id: athleteContext?.value,
        athlete_name: athleteContext?.label,
      })

      if (error) throw error

      return true
    } catch (error) {
      console.error('Error creating conversation:', error)
      return false
    } finally {
      isCreatingConversationRef.current = false
    }
  }

  const handleConversationSelect = (conversationId: string) => {
    setActiveConversationId(conversationId)
    chatSessionKeyRef.current = conversationId
    // Clear chat to load new conversation messages
    if (deepChatRef.current) {
      deepChatRef.current.clearMessages()
    }
    // Load conversation history from database
    loadConversationMessages(conversationId)
  }

  const removeBadge = (index: number) => {
    setContextBadges(contextBadges.filter((_, i) => i !== index))
  }

  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenMenuId(null)

    try {
      const { error } = await supabase.from('ai_conversations').delete().eq('id', conversationId)

      if (error) throw error

      // Remove from local state
      setConversations(conversations.filter((c) => c.id !== conversationId))

      // If this was the active conversation, clear it
      if (activeConversationId === conversationId) {
        setActiveConversationId(null)
        if (deepChatRef.current) {
          deepChatRef.current.clearMessages()
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error)
      alert('Failed to delete conversation')
    }
  }

  const toggleMenu = (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === conversationId ? null : conversationId)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null)
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [openMenuId])

  // Generate conversation title after first interaction
  const generateConversationTitle = async (conversationId: string) => {
    console.log('generateConversationTitle:', conversationId)
    if (!session || hasGeneratedTitleRef.current.has(conversationId)) {
      return
    }

    try {
      // Get messages from DeepChat
      const messages = deepChatRef.current?.getMessages()
      if (!messages || messages.length < 2) {
        // Need at least one user message and one AI response
        return
      }

      // Convert DeepChat messages to the format expected by the edge function
      const formattedMessages = messages.map((msg: any) => ({
        role: msg.role || 'user',
        content: msg.text || '',
      }))

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('generate-conversation-title', {
        body: { messages: formattedMessages },
      })
      if (error) {
        throw new Error(`Failed to generate conversation title: ${error.message}`)
      }
      const title = data.title || 'Untitled Conversation'

      // Update the conversation title in the database
      const { error: updateError } = await supabase.from('ai_conversations').update({ title }).eq('id', conversationId)

      if (updateError) {
        throw updateError
      }

      // Update local state
      setConversations((prev) => prev.map((conv) => (conv.id === conversationId ? { ...conv, title } : conv)))

      // Mark this conversation as having a generated title
      hasGeneratedTitleRef.current.add(conversationId)
    } catch (error) {
      console.error('Error generating conversation title:', error)
    }
  }

  // Handle new messages - save to database and trigger title generation after first AI response
  const handleNewMessage = useCallback(
    async (event: any) => {
      // Don't save messages while loading history
      if (isLoadingHistoryRef.current) return

      console.log('New message event:', event)

      if (!event?.message) return

      const message = event.message
      const role = message.role === 'ai' ? 'assistant' : 'user'
      const content = message.text || ''

      const conversationId = activeConversationId

      if (!conversationId) return

      // Check if this message has already been saved
      const messageKey = `${conversationId}-${role}-${content}`

      if (savedMessagesRef.current.has(messageKey)) {
        // Message already saved, skip
        return
      }

      // If this conversation doesn't exist in DB yet, create it
      if (conversationNotInListRef.current.has(conversationId) && role === 'user') {
        const created = await createConversationInDB(conversationId)
        if (!created) return
      }

      // Save message to database
      await saveMessage(conversationId, role, content)

      // Track that we've saved this message
      savedMessagesRef.current.add(messageKey)

      // After first AI response, add to list and generate title
      if (role === 'assistant' && conversationNotInListRef.current.has(conversationId)) {
        // Fetch the conversation from DB to add it to the list
        const { data: conv, error } = await supabase
          .from('ai_conversations')
          .select('*')
          .eq('id', conversationId)
          .single()

        if (!error && conv) {
          setConversations((prev) => [conv, ...prev])
          conversationNotInListRef.current.delete(conversationId)

          // Generate title after a short delay
          setTimeout(() => {
            generateConversationTitle(conversationId)
          }, 500)
        }
      }
    },
    [activeConversationId, contextBadges, user]
  )

  // Intercept requests to add conversation context
  const updateRequestDetails = useCallback(
    (requestDetails: any) => {
      if (!requestDetails.headers) {
        requestDetails.headers = {}
      }

      // Add conversation ID
      if (activeConversationId) {
        requestDetails.headers['x-conversation-id'] = activeConversationId
      }

      // Add athlete context
      const athleteContext = contextBadges.find((b) => b.type === 'athlete')
      if (athleteContext) {
        requestDetails.headers['x-athlete-id'] = athleteContext.value
      }

      return requestDetails
    },
    [activeConversationId, contextBadges]
  )

  useEffect(() => {
    if (deepChatRef.current) {
      deepChatRef.current.requestInterceptor = updateRequestDetails
    }
  }, [updateRequestDetails])

  const activeConversation = conversations.find((c) => c.id === activeConversationId)

  // Memoize DeepChat configuration to prevent re-initialization on every render
  const deepChatStyle = useMemo(
    () => ({
      border: 'none',
      height: 'calc(100vh - 66px)',
      width: 'calc(100vw - 300px)',
      paddingTop: '10px',
      backgroundColor: 'var(--color-background)',
    }),
    []
  )

  const deepChatMessageStyles = useMemo(
    () => ({
      default: {
        shared: {
          innerContainer: {
            fontSize: '0.9rem',
            padding: '0.5rem 0.75rem',
          },
          bubble: {
            maxWidth: '70%',
          },
        },
        user: {
          bubble: {
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-text-inverted)',
          },
        },
        ai: {
          bubble: {
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
          },
        },
      },
    }),
    []
  )

  const deepChatAvatars = useMemo(() => ({ ai: { src: '/logo_512.png' } }), [])

  const deepChatInputAreaStyle = useMemo(
    () => ({
      fontSize: '1rem',
      marginBottom: '100px',
      padding: '0.5rem 0.75rem',
      borderRadius: '1rem',
    }),
    []
  )

  const deepChatTextInput = useMemo(
    () => ({
      placeholder: { text: 'Ask me any question', style: { borderRadius: '2rem' } },
    }),
    []
  )

  const deepChatConnect = useMemo(
    () => ({
      url: `${supabaseUrl}/functions/v1/deepchat`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      stream: {
        readable: true,
        partialRender: true,
      },
    }),
    [session?.access_token]
  )

  const deepChatRequestBodyLimits = useMemo(
    () => ({
      maxMessages: 20,
      totalMessagesMaxCharLength: 100000,
    }),
    []
  )

  return (
    <Container>
      <Sidebar>
        <SidebarHeader>
          <Button
            variant="default"
            size="md"
            style={{ width: '100%', textAlign: 'start' }}
            onClick={createNewConversation}
          >
            <SquarePen size={16} />
            New Conversation
          </Button>
        </SidebarHeader>
        <SidebarTitle>Conversations</SidebarTitle>
        <ConversationList>
          {conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              $isActive={conversation.id === activeConversationId}
              onClick={() => handleConversationSelect(conversation.id)}
            >
              <ConversationHeader>
                <ConversationTitle>{conversation.title}</ConversationTitle>
                <ConversationMenuButton
                  className="conversation-menu-button"
                  onClick={(e) => toggleMenu(conversation.id, e)}
                >
                  <MoreVertical size={16} />
                </ConversationMenuButton>
              </ConversationHeader>
              {conversation.athlete_name && (
                <ConversationAthlete>
                  <User size={12} />
                  {conversation.athlete_name}
                </ConversationAthlete>
              )}
              {conversation.last_message && <ConversationPreview>{conversation.last_message}</ConversationPreview>}

              <ConversationMenu $isOpen={openMenuId === conversation.id}>
                <MenuItem className="danger" onClick={(e) => handleDeleteConversation(conversation.id, e)}>
                  <Trash2 size={14} />
                  Delete
                </MenuItem>
              </ConversationMenu>
            </ConversationItem>
          ))}
        </ConversationList>
      </Sidebar>

      <MainContent>
        {activeConversation || !conversations.length || activeConversationId === null ? (
          <>
            <ChatContainer>
              <DeepChat
                key={chatSessionKeyRef.current}
                ref={deepChatRef}
                style={deepChatStyle}
                messageStyles={deepChatMessageStyles}
                avatars={deepChatAvatars}
                inputAreaStyle={deepChatInputAreaStyle}
                textInput={deepChatTextInput}
                connect={deepChatConnect}
                requestBodyLimits={deepChatRequestBodyLimits}
                {...(conversationHistory.length > 0 ? { history: conversationHistory } : {})}
                onMessage={handleNewMessage}
              />
            </ChatContainer>
            <ChatFooter>
              {contextBadges.length > 0 && (
                <ContextBadgesContainer>
                  {contextBadges.map((badge, index) => (
                    <Badge key={`b_${index.toFixed()}`} style={{ backgroundColor: `${badge.type}30` }}>
                      <User size={12} />
                      {badge.label}
                      <BadgeRemoveButton onClick={() => removeBadge(index)}>
                        <X size={12} />
                      </BadgeRemoveButton>
                    </Badge>
                  ))}
                </ContextBadgesContainer>
              )}
            </ChatFooter>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>
              <MessageSquare size={64} />
            </EmptyStateIcon>
            <EmptyStateText>Select a conversation or create a new one to start analyzing athlete data</EmptyStateText>
          </EmptyState>
        )}
      </MainContent>
    </Container>
  )
}
